import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Table } from "@tanstack/react-table";
import { Cross2Icon } from "@radix-ui/react-icons";
import { OpenAPI } from "@/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTableViewOptions } from "@/components/ClientsheetTable/data-table-view-options";
import { InvoicePublic } from "./schema";
import { SelectInvoiceTypeComboBox } from "./SelectInvoiceTypeCombobox";
import { SelectMonthComboBox } from "./SelectMonthCombobox";
import { SelectYearComboBox } from "./SelectYearCombobox";

interface InvoiceData {
  data?: InvoicePublic[];
}

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  invoiceData: InvoiceData | undefined;
  isLoading: boolean;
  invoiceType: string | null;
  setInvoiceType: (value: string | null) => void;
  month: number | null;
  setMonth: (value: number | null) => void;
  year: number | null;
  setYear: (value: number | null) => void;
}

export function DataTableToolbar({
  table,
  invoiceData,
  isLoading,
  invoiceType,
  setInvoiceType,
  month,
  setMonth,
  year,
  setYear,
}: DataTableToolbarProps<InvoicePublic>) {
  const { t } = useTranslation();
  const isFiltered =
    table.getState().columnFilters.length > 0 || invoiceType !== null || month !== null || year !== null;
  const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original.id);
  const [isZipDownloading, setIsZipDownloading] = useState(false);

  const resetFilters = () => {
    table.resetColumnFilters();
    setInvoiceType(null);
    setMonth(null);
    setYear(null);
  };

  return (
    <div className="flex flex-col items-start justify-between gap-4 py-2 lg:flex-row lg:items-center">
      <div className="flex w-full flex-col items-stretch gap-2 lg:w-auto lg:flex-row lg:items-center">
        <Input
          placeholder={t("Filter by filename...")}
          value={(table.getColumn("id")?.getFilterValue() as string) ?? ""}
          onChange={(event) => table.getColumn("id")?.setFilterValue(event.target.value)}
          className="h-8 w-full lg:w-[250px]"
        />
        <SelectInvoiceTypeComboBox
          data={invoiceData}
          isLoading={isLoading}
          value={invoiceType}
          placeholder={t("Invoice type")}
          onChange={setInvoiceType}
          triggerClassName="h-8 w-full lg:w-[180px]"
        />
        <SelectYearComboBox
          data={invoiceData}
          isLoading={isLoading}
          value={year}
          placeholder={t("Year")}
          onChange={setYear}
          triggerClassName="h-8 w-full lg:w-[100px]"
        />
        <SelectMonthComboBox
          data={invoiceData}
          isLoading={isLoading}
          value={month}
          placeholder={t("Month")}
          onChange={setMonth}
          triggerClassName="h-8 w-full lg:w-[120px]"
        />

        {isFiltered && (
          <Button variant="ghost" onClick={resetFilters} className="h-8 w-full px-2 lg:w-auto lg:px-3">
            {t("Reset")}
            <Cross2Icon className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="flex w-full flex-row justify-end gap-2 lg:w-auto lg:justify-start">
        <Button
          variant="secondary"
          size="sm"
          disabled={selectedRows.length === 0 || isZipDownloading}
          onClick={async () => {
            setIsZipDownloading(true);
            const queryParams = new URLSearchParams();
            selectedRows.forEach((row) => {
              queryParams.append("ids", row);
            });

            const response = await fetch(`${OpenAPI.BASE}/api/v1/invoice/export?${queryParams}`, {
              method: "GET",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              },
              credentials: "include",
            });

            if (!response.ok) {
              setIsZipDownloading(false);
              console.error("Download failed");
              return;
            }

            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = url;
            a.download = `${t("BCI-Invoices")}-${new Date().toISOString().split("T")[0]}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            table.resetRowSelection();
            setIsZipDownloading(false);
          }}
          className="w-full lg:w-auto"
        >
          {isZipDownloading ? t("Downloading...") : t("Download selected")}
        </Button>

        <DataTableViewOptions table={table} />
      </div>
    </div>
  );
}
